import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress, useTheme, Button, Chip, Stack } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CircleIcon from '@mui/icons-material/Circle';
import GensizeStepper from '@/modules/Indigent/components/Stepper';
import IndigentActionButtons from '@/modules/Indigent/components/IndigentActionButtons';
import StepPersonalInfo, { type StepPersonalInfoRef } from './components/StepPersonalInfo';
import StepAddressServices, { type StepAddressServicesRef } from './components/StepAddressServices';
import IndigentFormFooter from './components/IndigentFormFooter';
import StepSupportingDocuments from './components/StepSupportingDocs';
import StepDeclarationReview from './components/StepDeclarationReview';
import ReviewDialog from './components/ReviewDialog';
import { useNavigate } from 'react-router-dom';
import {
    indigentService,
    type IndigentFormData,
    type Occupant,
} from '@/services/indigentService';
import { fileService } from '@/services/fileService';
import { useMediaQuery } from '@mui/system';
import { useResponsiveToast } from '@/components/Toast';

// Status colors for the status badge - support both API and UI formats
const statusColors = {
    // UI format (from ApprovalCard)
    Approved: {
        bg: '#D1FADF',
        color: '#027A48',
        dot: '#027A48',
    },
    Pending: {
        bg: '#FFF1C0',
        color: '#A3871E',
        dot: '#CFAC2C',
    },
    Rejected: {
        bg: '#FEE2E2',
        color: '#DC2626',
        dot: '#DC2626',
    },
    'Review Passed': {
        bg: '#DBEAFE',
        color: '#1D4ED8',
        dot: '#1D4ED8',
    },
    'Review Failed': {
        bg: '#FEF3C7',
        color: '#D97706',
        dot: '#D97706',
    },
    // API format (lowercase)
    approved: {
        bg: '#D1FADF',
        color: '#027A48',
        dot: '#027A48',
    },
    pending: {
        bg: '#FFF1C0',
        color: '#A3871E',
        dot: '#CFAC2C',
    },
    rejected: {
        bg: '#FEE2E2',
        color: '#DC2626',
        dot: '#DC2626',
    },
    reviewed: {
        bg: '#DBEAFE',
        color: '#1D4ED8',
        dot: '#1D4ED8',
    },
    failed: {
        bg: '#FEF3C7',
        color: '#D97706',
        dot: '#D97706',
    },
};

// Helper function to transform API status to display format
const transformStatusForDisplay = (apiStatus: string): string => {
    switch (apiStatus) {
        case 'pending':
            return 'Pending';
        case 'reviewed':
            return 'Review Passed';
        case 'approved':
            return 'Approved';
        case 'failed':
            return 'Review Failed';
        case 'rejected':
            return 'Rejected';
        default:
            return apiStatus; // Return as-is if no transformation needed
    }
};

// Interface for the actual API response
interface ApiIndigentResponse {
    id: string;
    indigent_number: string;
    account_number: string;
    sa_id: string;
    name: string;
    surname: string;
    employer_name?: string;
    source_of_income?: string;
    monthly_income?: number;
    cell_number?: string;
    email?: string;
    erf_no: string;
    ward_no: string;
    address_line_1?: string;
    address_line_2?: string;
    address_line_3?: string;
    town: string;
    electricity_meter_no: string;
    legacy_application_number?: string;
    service_electricity: boolean;
    service_water: boolean;
    service_waste: boolean;
    service_waste_water: boolean;
    status: string;
    financial_year: number;
    declaration_accepted: boolean;
    submitted_by: string;
    updated_by: string | null;
    submitted_by_username: string | null;
    created_at: string;
    updated_at: string;
    approved_by: string | null;
    approved_at: string | null;
    rejected_by: string | null;
    rejected_at: string | null;
    review_remark: string | null;
    signature_file?: string; // Add signature file path
    occupants: Array<{
        name: string;
        surname: string;
        personal_id: string;
        employer_name?: string;
        monthly_income?: string;
        cell_number?: string;
        id: string;
        request_id: string;
        photo_path?: string;
    }>;
    attachments: Array<{
        id: string;
        request_id: string;
        filename: string;
        file_path: string;
        file_type: string;
        document_type: string;
        uploaded_by: string;
        uploaded_at: string;
    }>;
}

// Helper function to fetch file from path using fileService
const fetchFileFromPath = async (filePath: string, filename: string): Promise<File> => {
    try {
        const response = await fileService.previewFile(filePath);
        
        // Get MIME type from response or fallback to path-based detection
        let mimeType = response.data.type || 'application/octet-stream';
        
        // If the response doesn't provide a valid MIME type, try to detect from file path
        if (!mimeType || mimeType === 'application/octet-stream') {
            const fileExtension = filePath.split('.').pop()?.toLowerCase() || '';
            
            // Map common file extensions to MIME types
            if (['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp'].includes(fileExtension)) {
                mimeType = `image/${fileExtension === 'jpg' ? 'jpeg' : fileExtension}`;
            } else if (fileExtension === 'pdf') {
                mimeType = 'application/pdf';
            } else if (fileExtension === 'doc') {
                mimeType = 'application/msword';
            } else if (fileExtension === 'docx') {
                mimeType = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
            }
        }
        
        return new File([response.data], filename, { type: mimeType });
    } catch (error) {
        console.error('Error fetching file:', error);
        // Return a placeholder file if fetch fails
        return new File([''], filename, { type: 'application/octet-stream' });
    }
};

interface FormOccupant {
    name: string;
    surname: string;
    personalId: string;
    employerName?: string;
    monthlyIncome: string;
    cellNumber?: string | undefined; // Optional for demo subdomain
    documents?: File[];
    photoPath?: string; // Add photoPath for existing occupant photos
}

export interface FormData {
    // Step 1
    financialYear: string;
    name: string;
    surname: string;
    personalId: string;
    employerName: string;
    income: string;
    incomeSource: string;
    cellNumber?: string | undefined; // Optional for demo subdomain
    email: string;
    services: string[];

    // Step 2
    ward: string;
    town: string;
    erfNo: string;
    address1: string;
    address2: string;
    address3: string;
    accountNumber: string;
    electricityMeterNo: string;
    occupants?: FormOccupant[];
    occupantPhotos?: File[];

    // Step 3 - Supporting Documents
    supportingDocs?: { [key: string]: File[] };
    document_types: string[];
    files: File[];
    attachments?: Array<{
        id: string;
        filename: string;
        file_path: string;
        file_type: string;
        document_type: string;
    }>; // Match the API response structure
    deletedAttachmentIds?: string[]; // Track deleted attachment IDs for PATCH API

    // Step 4 - Declaration
    legacyApplicationNumber?: string;
    signatureImage: File | null;
    declarationAccepted: boolean;
}

interface CreateIndigentFormProps {
    mode?: 'create' | 'edit';
    requestId?: string;
    initialFormData?: Partial<FormData>;
}

const CreateIndigentForm: React.FC<CreateIndigentFormProps> = ({ mode = 'create', requestId, initialFormData }) => {
    const [activeStep, setActiveStep] = useState(0);
    const [showReviewDialog, setShowReviewDialog] = useState(false);
    const [isLoading, setIsLoading] = useState(mode === 'edit');
    const [isLoadingData, setIsLoadingData] = useState(mode === 'edit');
    const [isInitialLoadComplete, setIsInitialLoadComplete] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false); // Add loading state for submit operation
    const [isEditable, setIsEditable] = useState(mode === 'create'); // New state for edit mode
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { success, error: showError } = useResponsiveToast();
    
    // Store indigent number for edit mode
    const [indigentNumber, setIndigentNumber] = useState<string>('');
    
    // Store indigent status for action buttons
    const [indigentStatus, setIndigentStatus] = useState<string>('');
    
    // Ref to prevent double fetching in React Strict Mode
    const hasFetchedFiles = useRef(false);
    
    // Ref for StepPersonalInfo component
    const stepPersonalInfoRef = useRef<StepPersonalInfoRef>(null);
    
    // Ref for StepAddressServices component
    const stepAddressServicesRef = useRef<StepAddressServicesRef>(null);

    // Step-wise form data state
    const [formData, setFormData] = useState<FormData>(() => ({
        // Step 1
        financialYear: '',
        name: '',
        surname: '',
        personalId: '',
        employerName: '',
        income: '',
        incomeSource: '',
        cellNumber: '',
        email: '',
        services: ['electricity'],

        // Step 2
        ward: '',
        town: '',
        erfNo: '',
        address1: '',
        address2: '',
        address3: '',
        accountNumber: '',
        electricityMeterNo: '',
        occupants: [],
        occupantPhotos: [],

        // Step 3
        supportingDocs: {},
        document_types: [],
        files: [],
        deletedAttachmentIds: [], // Initialize empty array for tracking deleted attachments

        // Step 4
        signatureImage: null,
        declarationAccepted: false,
        ...(initialFormData || {}),
    }));

    // Step-wise validation flags - all start as false since nothing is validated initially
    const [stepValidations, setStepValidations] = useState<boolean[]>([false, false, false, false]);

    const fetchIndigentData = useCallback(async () => {
        if (!requestId || hasFetchedFiles.current) return;

        try {
            setIsLoadingData(true);
            hasFetchedFiles.current = true; // Prevent double fetching
            
            const response = await indigentService.getIndigentById(requestId);
            const apiData = response as unknown as ApiIndigentResponse;

            // Store indigent number for edit mode
            setIndigentNumber(apiData.indigent_number);
            
            // Store indigent status for action buttons
            setIndigentStatus(apiData.status);

            // Transform API data to form data format (without files initially)
            const transformedData: FormData = {
                // Step 1
                financialYear: apiData?.financial_year ? apiData.financial_year.toString() : '',
                name: apiData.name,
                surname: apiData.surname,
                personalId: apiData.sa_id,
                employerName: apiData.employer_name || '',
                income: apiData.monthly_income !== undefined && apiData.monthly_income !== null ? apiData.monthly_income.toString() : '',
                incomeSource: apiData.source_of_income || '',
                cellNumber: apiData.cell_number || '',
                email: apiData.email || '',
                services: [
                    ...(apiData.service_electricity ? ['electricity'] : []),
                    ...(apiData.service_water ? ['water'] : []),
                    ...(apiData.service_waste ? ['waste'] : []),
                    ...(apiData.service_waste_water ? ['wastewater'] : []),
                ],

                // Step 2
                ward: apiData.ward_no,
                town: apiData.town,
                erfNo: apiData.erf_no,
                address1: apiData.address_line_1 || '',
                address2: apiData.address_line_2 || '',
                address3: apiData.address_line_3 || '',
                accountNumber: apiData.account_number,
                electricityMeterNo: apiData.electricity_meter_no,
                occupants:
                    apiData.occupants?.map(occ => ({
                        name: occ.name,
                        surname: occ.surname,
                        personalId: occ.personal_id,
                        employerName: occ.employer_name || '',
                        monthlyIncome: occ.monthly_income !== undefined && occ.monthly_income !== null ? occ.monthly_income.toString() : '',
                        cellNumber: occ.cell_number || '',
                        photoPath: occ.photo_path,
                    })) || [],
                occupantPhotos: [], // Initialize empty, will be populated in background

                // Step 3
                supportingDocs: {},
                document_types: apiData.attachments?.map(att => att.document_type) || [], // Extract from attachments
                files: [], // Initialize empty, will be populated in background
                attachments: apiData.attachments || [],
                deletedAttachmentIds: [], // Initialize empty array for tracking deleted attachments

                // Step 4
                legacyApplicationNumber: apiData.legacy_application_number || '',
                signatureImage: null, // Initialize null, will be populated in background
                declarationAccepted: apiData.declaration_accepted || false,
            };

            // Set the form data immediately to unblock the page
            setFormData(transformedData);
            setIsLoadingData(false);
            setIsLoading(false);

            // Now fetch files in the background
            const fetchPromises: Promise<{ type: 'attachment' | 'occupant' | 'signature'; file: File; index?: number } | null>[] = [];

            // Add attachment file fetch promises
            if (apiData.attachments && apiData.attachments.length > 0) {
                apiData.attachments.forEach((attachment, index) => {
                    fetchPromises.push(
                        fetchFileFromPath(attachment.file_path, attachment.filename)
                            .then(file => ({ type: 'attachment' as const, file, index }))
                            .catch(error => {
                                console.error(`Error fetching attachment ${attachment.filename}:`, error);
                                return null;
                            })
                    );
                });
            }

            // Add occupant photo fetch promises
            if (apiData.occupants && apiData.occupants.length > 0) {
                apiData.occupants.forEach((occupant, index) => {
                    if (occupant.photo_path) {
                        const actualFilename = occupant.photo_path.split('/').pop() || `occupant_${occupant.name}_${occupant.surname}`;
                        fetchPromises.push(
                            fetchFileFromPath(occupant.photo_path, actualFilename)
                                .then(file => ({ type: 'occupant' as const, file, index }))
                                .catch(error => {
                                    console.error(`Error fetching occupant photo for ${occupant.name}:`, error);
                                    return null;
                                })
                        );
                    }
                });
            }

            // Add signature file fetch promise
            if (apiData.signature_file) {
                const signaturePath = apiData.signature_file;
                const signatureFilename = signaturePath.split('/').pop() || 'signature';
                
                fetchPromises.push(
                    fetchFileFromPath(signaturePath, signatureFilename)
                        .then(file => ({ type: 'signature' as const, file }))
                        .catch(error => {
                            console.error('Error fetching signature file:', error);
                            return null;
                        })
                );
            }

            // Execute all fetch promises in parallel in the background
            Promise.all(fetchPromises).then(fetchResults => {
                // Separate results by type
                const fetchedFiles: File[] = [];
                const fetchedOccupantPhotos: File[] = [];
                let fetchedSignatureFile: File | null = null;

                fetchResults.forEach(result => {
                    if (result) {
                        if (result.type === 'attachment') {
                            fetchedFiles.push(result.file);
                        } else if (result.type === 'occupant') {
                            fetchedOccupantPhotos.push(result.file);
                        } else if (result.type === 'signature') {
                            fetchedSignatureFile = result.file;
                        }
                    }
                });

                // Update form data with fetched files
                setFormData(prevData => ({
                    ...prevData,
                    files: fetchedFiles,
                    occupantPhotos: fetchedOccupantPhotos,
                    signatureImage: fetchedSignatureFile,
                }));
            }).catch(error => {
                console.error('Error fetching files in background:', error);
            });

        } catch (error) {
            console.error('Error fetching indigent data:', error);
            showError('Error loading indigent data. Please try again.');
            setIsLoadingData(false);
            setIsLoading(false);
        }
    }, [requestId, showError]);

    // Fetch indigent data for edit mode
    useEffect(() => {
        if (mode === 'edit' && requestId) {
            fetchIndigentData();
        }
    }, [mode, requestId, fetchIndigentData]);

    // Reset fetch flag when requestId changes or component unmounts
    useEffect(() => {
        return () => {
            hasFetchedFiles.current = false;
        };
    }, [requestId]);

    // Set validation to true for all steps when in edit mode and data is loaded
    useEffect(() => {
        if (mode === 'edit' && !isLoadingData && formData.name) {
            setStepValidations([true, true, true, true]);
            setIsInitialLoadComplete(true);
        }
    }, [mode, isLoadingData, formData.name]);

    const updateValidation = useCallback(
        (stepIndex: number, isValid: boolean) => {
            setStepValidations(prev => {
                // In edit mode, don't allow validation updates until initial load is complete
                if (mode === 'edit' && !isInitialLoadComplete) {
                    return prev;
                }
                const updated = [...prev];
                updated[stepIndex] = isValid;
                return updated;
            });
        },
        [mode, isInitialLoadComplete],
    );

    // Memoize validation change handlers to prevent infinite re-renders
    const handleStep0Validation = useCallback(
        (valid: boolean) => {
            updateValidation(0, valid);
        },
        [updateValidation],
    );

    const handleStep1Validation = useCallback(
        (valid: boolean) => {
            updateValidation(1, valid);
        },
        [updateValidation],
    );

    const handleStep2Validation = useCallback(
        (valid: boolean) => {
            updateValidation(2, valid);
        },
        [updateValidation],
    );

    const handleStep3Validation = useCallback(
        (valid: boolean) => {
            updateValidation(3, valid);
        },
        [updateValidation],
    );

    const handleFormDataUpdate = useCallback((data: Partial<typeof formData>) => {
        setFormData(prev => {
            const updated = { ...prev, ...data };

            // Preserve signature if it exists in prev but not in data
            if (
                prev.signatureImage &&
                !data.signatureImage &&
                !data.hasOwnProperty('signatureImage')
            ) {
                updated.signatureImage = prev.signatureImage;
            }

            return updated;
        });
    }, []);

    const steps = [
        <StepPersonalInfo
            key='personal'
            ref={stepPersonalInfoRef}
            formData={formData}
            setFormData={handleFormDataUpdate}
            onValidationChange={handleStep0Validation}
            mode={mode}
            isEditable={isEditable}
            indigentNumber={indigentNumber}
        />,
        <StepAddressServices
            key='address'
            ref={stepAddressServicesRef}
            formData={formData}
            setFormData={handleFormDataUpdate}
            onValidationChange={handleStep1Validation}
            mode={mode}
            financialYear={formData.financialYear}
            isEditable={isEditable}
            indigentNumber={indigentNumber}
        />,
        <StepSupportingDocuments
            key='documents'
            formData={formData}
            setFormData={handleFormDataUpdate}
            onValidationChange={handleStep2Validation}
            mode={mode}
            isEditable={isEditable}
        />,
        <StepDeclarationReview
            key='review'
            formData={formData}
            setFormData={handleFormDataUpdate}
            onValidationChange={handleStep3Validation}
            mode={mode}
            isEditable={isEditable}
        />,
    ];

    // When prefilled data is present, trigger validation on both steps so Next is enabled without user edits
    useEffect(() => {
        if (initialFormData) {
            // Allow children to mount and refs to be set
            const timer = setTimeout(async () => {
                try {
                    const [personalValid, addressValid] = await Promise.all([
                        stepPersonalInfoRef.current?.runValidation?.() ?? Promise.resolve(false),
                        stepAddressServicesRef.current?.runValidation?.() ?? Promise.resolve(false),
                    ]);
                    setStepValidations(prev => {
                        const next = [...prev];
                        next[0] = Boolean(personalValid);
                        next[1] = Boolean(addressValid);
                        return next;
                    });
                } catch (e) {
                    // no-op
                }
            }, 0);
            return () => clearTimeout(timer);
        }
    }, [initialFormData]);

    const handleBack = useCallback(() => {
        if (activeStep > 0) {
            setActiveStep(prev => prev - 1);
        }
    }, [activeStep]);

    const handleSubmitClick = useCallback(() => {
        setShowReviewDialog(true);
    }, []);

    const handleReviewClose = useCallback(() => {
        setShowReviewDialog(false);
    }, []);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true); // Set loading state at the beginning
        
        // Get the current form data to avoid stale closure issues
        const currentFormData = await new Promise<FormData>(resolve => {
            setFormData(current => {
                resolve(current);
                return current;
            });
        });

        try {
            // Transform occupants data to match API format
            const occupants: Occupant[] =
                currentFormData.occupants?.map((occ, index) => ({
                    name: occ.name,
                    surname: occ.surname,
                    personal_id: occ.personalId,
                    employer_name: occ.employerName || null,
                    monthly_income: occ.monthlyIncome || null,
                    cell_number: occ.cellNumber || null,
                })) || [];

            // Transform formData to match API requirements
            const submitData: IndigentFormData = {
                ward_no: currentFormData.ward,
                electricity_meter_no: currentFormData.electricityMeterNo,
                cell_number: currentFormData.cellNumber || undefined,
                email: currentFormData.email || undefined,
                erf_no: currentFormData.erfNo,
                service_electricity: currentFormData.services.includes('electricity'),
                service_waste_water: currentFormData.services.includes('wastewater'),
                service_water: currentFormData.services.includes('water'),
                service_waste: currentFormData.services.includes('waste'),
                name: currentFormData.name,
                sa_id: currentFormData.personalId,
                source_of_income: currentFormData.incomeSource,
                legacy_application_number: currentFormData.legacyApplicationNumber,
                employer_name: currentFormData.employerName,
                occupants,
                address_line_1: currentFormData.address1,
                address_line_2: currentFormData.address2,
                address_line_3: currentFormData.address3,
                town: currentFormData.town,
                financial_year: currentFormData.financialYear,
                document_types: currentFormData.document_types?.join(',') || '',
                surname: currentFormData.surname,
                account_number: currentFormData.accountNumber,
                monthly_income: currentFormData.income,
                declaration_accepted: currentFormData.declarationAccepted,
                signature_file: currentFormData.signatureImage || new File([], 'empty.txt'),
                // Filter out null/undefined occupant photos and ensure we have the correct files
                occupant_photos:
                    currentFormData.occupantPhotos?.filter(photo => photo instanceof File) || [],
                files: currentFormData.files?.filter(file => file instanceof File) || [],
            };

            let response;
            if (mode === 'edit' && requestId) {
                // Add indigent_number for edit mode
                const editData = {
                    ...submitData,
                    indigent_number: indigentNumber,
                };
                response = await indigentService.updateIndigent(requestId, editData);
                console.log('Indigent application updated successfully:', response);
                success('Indigent application updated successfully!');
            } else {
                response = await indigentService.createIndigent(submitData);
                success('Indigent application submitted successfully!');
            }

            // Redirect to approval page where the submitted application can be reviewed
            navigate('/approval');
        } catch (error) {
            console.error('Error submitting indigent application:', error);
            showError('Error submitting indigent application. Please try again.');
        } finally {
            setIsSubmitting(false); // Clear loading state
            setShowReviewDialog(false);
        }
    }, [navigate, setFormData, mode, requestId, indigentNumber, success, showError]); // Add indigentNumber to dependencies

    const handleStatusUpdate = useCallback(async () => {
        // Refresh the indigent data after status update
        if (mode === 'edit' && requestId) {
            try {
                const response = await indigentService.getIndigentById(requestId);
                const apiData = response as unknown as ApiIndigentResponse;
                setIndigentStatus(apiData.status);
            } catch (error) {
                console.error('Error refreshing indigent data:', error);
            }
        }
    }, [mode, requestId]);

    const handleNext = useCallback(async () => {
        if (activeStep < steps.length - 1) {
            // In read-only mode, just navigate to next step without validation
            if (!isEditable) {
                setActiveStep(prev => prev + 1);
                return;
            }
            
            // If we're on step 0 (Personal Info), validate Personal ID and Financial Year combination
            if (activeStep === 0 && (mode === 'create' || (mode === 'edit' && isEditable))) {
                setIsValidating(true);
                try {
                    const isValid = await stepPersonalInfoRef.current?.validatePersonalIdCombination();
                    if (isValid) {
                        setActiveStep(prev => prev + 1);
                    }
                } catch (error) {
                    console.error('Validation error:', error);
                } finally {
                    setIsValidating(false);
                }
            } 
            // If we're on step 1 (Address Services), validate Account Number and Electricity Meter Number combination
            else if (activeStep === 1 && (mode === 'create' || (mode === 'edit' && isEditable))) {
                setIsValidating(true);
                try {
                    const isValid = await stepAddressServicesRef.current?.validateAccountMeterCombination();
                    if (isValid) {
                        setActiveStep(prev => prev + 1);
                    }
                } catch (error) {
                    console.error('Validation error:', error);
                } finally {
                    setIsValidating(false);
                }
            } else {
                setActiveStep(prev => prev + 1);
            }
        } else {
            // Only show submit dialog if in edit mode
            if (isEditable) {
                handleSubmitClick();
            } else {
                // In read-only mode, just go back to first step or navigate away
                setActiveStep(0);
            }
        }
    }, [activeStep, steps.length, handleSubmitClick, mode, isEditable]);

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    backgroundColor: '#FEFBFF',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div style={{
            backgroundColor: '#FEFBFF',
            minHeight: window.innerWidth < 600 ? 'calc(100vh - 96px)' : '100vh',
            position: window.innerWidth < 600 ? 'absolute' : 'relative',
            top: window.innerWidth < 600 ? '86px' : 'auto',
            left: window.innerWidth < 600 ? '0' : 'auto',
            right: window.innerWidth < 600 ? '0' : 'auto',
            bottom: window.innerWidth < 600 ? '0' : 'auto',
            padding: '16px',
            paddingBottom: window.innerWidth < 600 ? '32px' : '16px',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            width: window.innerWidth < 600 ? '100vw' : '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            overflowX: 'hidden'
        }}>
            <div style={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                padding: isMobile?'16px':'32px',
                paddingBottom: window.innerWidth < 600 ? '24px' : '16px',
                width: '100%',
                maxWidth: '100%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: window.innerWidth < 600 ? '16px' : '0'
            }}>
                <Box sx={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    mb: isMobile ? 2 : 3 
                }}>
                    {mode === 'edit' ? (
                        <Stack direction="row" alignItems="center" spacing={2}>
                            <Typography
                                sx={{
                                    fontWeight: 600,
                                    color: '#132F5F',
                                    fontSize: isMobile ? '1.1rem' : '1.4rem',
                                }}
                            >
                                Indigent #{indigentNumber}
                            </Typography>
                            {indigentStatus && (
                                <Chip
                                    label={transformStatusForDisplay(indigentStatus)}
                                    icon={
                                        <CircleIcon
                                            sx={{ 
                                                color: statusColors[indigentStatus as keyof typeof statusColors]?.dot || '#666', 
                                                fontSize: 10 
                                            }}
                                        />
                                    }
                                    sx={{
                                        backgroundColor: statusColors[indigentStatus as keyof typeof statusColors]?.bg || '#f5f5f5',
                                        color: statusColors[indigentStatus as keyof typeof statusColors]?.color || '#666',
                                        fontWeight: 600,
                                        fontSize: isMobile ? 11 : 12,
                                        height: isMobile ? 28 : 32,
                                        borderRadius: '24px',
                                        px: 1,
                                        width: 'fit-content',
                                    }}
                                />
                            )}
                        </Stack>
                    ) : (
                        <Typography
                            sx={{
                                fontWeight: 600,
                                color: '#132F5F',
                                textAlign: 'center',
                                fontSize: isMobile ? '1.1rem' : '1.4rem',
                            }}
                        >
                            Create Indigent
                        </Typography>
                    )}
                    
                    {mode === 'edit' && indigentStatus !== 'approved' && (
                        <Button
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => setIsEditable(!isEditable)}
                            sx={{
                                borderColor: isEditable ? '#2D4677' : '#6B7280',
                                color: isEditable ? '#2D4677' : '#6B7280',
                                '&:hover': {
                                    borderColor: isEditable ? '#1a365d' : '#4B5563',
                                    backgroundColor: isEditable ? 'rgba(45, 70, 119, 0.04)' : 'rgba(107, 114, 128, 0.04)',
                                },
                            }}
                        >
                            {isEditable ? 'View Only' : 'Edit'}
                        </Button>
                    )}
                </Box>

                <GensizeStepper activeStep={activeStep} />

                <Box sx={{ mt: 10 }}>{steps[activeStep]}</Box>

                <Box sx={{ mt: 6 }}>
                    <IndigentFormFooter
                        onBack={handleBack}
                        onNext={handleNext}
                        isLastStep={activeStep === steps.length - 1}
                        activeStep={activeStep}
                        disableNext={!isEditable ? false : !stepValidations[activeStep]}
                        isLoading={isValidating}
                        isEditable={isEditable}
                        mode={mode}
                    />
                    
                    {/* Show action buttons in view-only mode */}
                    {mode === 'edit' && !isEditable && requestId && indigentStatus && (
                        <IndigentActionButtons
                            requestId={requestId}
                            status={indigentStatus}
                            onStatusUpdate={handleStatusUpdate}
                            indigentData={{
                                sa_id: formData.personalId,
                                name: formData.name,
                                surname: formData.surname,
                                address1: formData.address1,
                                address2: formData.address2,
                            }}
                            indigentNumber={indigentNumber}
                        />
                    )}
                </Box>
            </div>

            <ReviewDialog
                open={showReviewDialog}
                onClose={handleReviewClose}
                onSubmit={handleSubmit}
                formData={formData}
                isSubmitting={isSubmitting}
            />
        </div>
    );
};

export default CreateIndigentForm;
